import { breakpoints, colors, transitions } from '../settings';

/**
 * Simple CSS MediaQuery. Expects a breakpoint (string or number) and min or max string (default min).
 * @param {string} mediaQuery - Identifier for the breakpoint in the configuration file
 * @param {string} minMax - Default: min. String for the media-query type
 * @returns {string} Media query definition
 * @example ${mq('m', 'max')} { css styles go here };
 */
export function mq(mediaQuery, minMax = 'min') {
  if (typeof mediaQuery !== 'number' && typeof mediaQuery !== 'string') {
    throw new Error('mediaQuery must be a string or number');
  }

  const value = typeof mediaQuery === 'string' ? breakpoints[mediaQuery] : `${mediaQuery}px`;

  return `
    @media (${minMax}-width: ${value}) 
  `;
}
/**
 * Get transition statement
 * @param {string} id - Identifier for the transition in the configuration file. Default: 'default'
 * @param {Object} overrides - Object to override certain key value pairs
 * @returns {string} Transition statement
 * @example ${getTransition('slower', { duration: 0.8 })};
 */
export function getTransition(id = 'default', overrides = {}) {
  if (!Object.prototype.hasOwnProperty.call(transitions, id)) {
    throw new Error(`Transition ${id} not known`);
  }

  const config = {
    property: overrides.property || transitions[id].property,
    duration: overrides.duration || transitions[id].duration,
    easing: overrides.easing || transitions[id].easing,
    delay: overrides.delay || transitions[id].delay
  };

  return `
    transition: ${config.property} ${config.duration}s ${config.easing} ${
    config.delay ? `${config.delay}s` : ''
  };
  `;
}
/**
 * Returns color from settings referenced by id
 * @param  {string} id - Identifier for the color in the configuration map. Default: white
 * @returns {string} Resulting color from configuration file
 * @example color: ${getColor('white')};
 */
export function getColor(id = 'white') {
  if (!Object.prototype.hasOwnProperty.call(colors, id)) {
    throw new Error(`Color ${id} not known`);
  }
  return colors[id];
}

/**
 * CSS to cover an area.
 * @param {string} offset - Default: 0. Sets an offset to act as a margin
 * @returns {string}
 * @example ${cover('20px')};
 */
export function cover(offset = '0') {
  if (typeof cover !== 'string') {
    throw new Error('Argument must be a string');
  }
  return {
    position: 'absolute',
    top: offset,
    right: offset,
    bottom: offset,
    left: offset
  };
}

/**
 * Center children via flexbox.
 * @example ${centerChildren()};
 */
export function centerChildren() {
  return `
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  `;
}

/**
 * CSS to hide content visually but remain accessible to screen readers.
 * @returns {string}
 * @example ${hideVisually()}
 */
export function hideVisually() {
  return `
    border: 0;
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    white-space: nowrap;
    width: 1px
  `;
}

/**
 * https://ghinda.net/article/mimic-native-focus-css/
 * Emulate basic browser focus for custom form elements
 * @returns {string}
 * @example ${emulateFocus()}
 */

export function emulateFocus() {
  return `
  outline: 1px #000 dotted;
  @media (-webkit-min-device-pixel-ratio: 0) {
    outline: 5px -webkit-focus-ring-color auto;
  }
  `;
}

/**
 * CSS to represent truncated text with an ellipsis.
 * @param {string} max - Default: 100%. Sets the width of the text
 * @returns {string}
 * @example ${ellipsis('250px')}
 */
export function ellipsis(max = '100%') {
  if (typeof max !== 'string') {
    throw new Error('Argument must be a string');
  }
  return `
    display: inline-block;
    maxWidth: ${max};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    word-wrap: normal;
  `;
}

/**
 * CSS to reset button styling.
 * @returns {string}
 * @example ${btnReset()}
 */
export function btnReset() {
  return `
    appearance: none;
    border: 0;
    margin: 0;
    padding: 0;
    background: transparent;
    cursor: pointer;
  }
  `;
}

/**
 * Transform px value to rem value.
 * @param {string} px - PX value as a string. e.g. '12px'
 * @returns {string}
 * @example ${px2rem('25px')}
 */
export const px2rem = px => `${parseInt(px.replace('px', '')) / 16}rem`;

export const hexToRgbA = (hex, opacity = 1) => {
  let c;
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split('');
    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    }
    c = '0x' + c.join('');
    return 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + ',' + opacity + ')';
  }
  throw new Error('Bad Hex');
};
